import { useToastStore } from "../stores/toast";

/**
 * Extracts a user-facing message from a structured API error and pushes it to
 * the toast store.
 *
 * The backend may return a `translation` object with an i18n key and
 * interpolation args. When present, we attempt to translate it. If the i18n
 * library returns the key unchanged (meaning the key is missing), we fall back
 * to the plain-English `human` field. If neither is available, we show a
 * generic error message.
 *
 * @param {object|null} error - Superagent error response (`e.response`).
 * @param {function} t - i18next translation function.
 */
/**
 * True when a request failed without any server response (connection lost,
 * DNS failure, timeout) — as opposed to an HTTP error status. Service
 * functions return the raw superagent error in that case, which has no
 * `status` property.
 *
 * @param {object|null} error - Error returned by a service call.
 */
export function isNetworkError(error) {
  return !!error && error.status === undefined;
}

export function showApiError(error, t) {
  try {
    // FastAPI wraps HTTPException detail in {"detail": {...}}
    const body = error?.body?.detail ?? error?.body;
    const translation = body?.translation;

    // Backend list convention: detail=[{"t": "i18n.key"}]
    if (Array.isArray(body) && body[0]?.t) {
      const translated = t(body[0].t);
      if (translated !== body[0].t) {
        useToastStore.getState().addToast("error", translated);
        return;
      }
    }

    if (translation?.id && translation?.args) {
      const translated = t(translation.id, translation.args);
      // i18n returns the key itself when the translation is missing
      if (translated !== translation.id) {
        useToastStore.getState().addToast("error", translated);
        return;
      }
    }

    if (body?.human) {
      useToastStore.getState().addToast("error", body.human);
      return;
    }

    useToastStore.getState().addToast("error", t("errors.unknown"));
  } catch {
    useToastStore.getState().addToast("error", t("errors.unknown"));
  }
}
